import React, { useContext, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import axios from 'axios'
import swal from 'sweetalert2'
import { Usercntxt } from '../../context/UserContext'
import format from 'date-fns/format'

/* ───────────────────── Constants & Helpers ───────────────────── */
const ORDERS_CACHE_MUTATION_KEY = 'orders_cache_mutation'
const ORDERS_CACHE_CHANNEL = 'orders_cache_channel'

const getOrdersTabId = () => {
  let tabId = sessionStorage.getItem('orders_tab_id')

  if (!tabId) {
    tabId = `${Date.now()}_${Math.random().toString(36).slice(2)}`
    sessionStorage.setItem('orders_tab_id', tabId)
  }

  return tabId
}

const getCurrentUserIdForMutation = () => {
  return window.__currentUserId || localStorage.getItem('current_user_id') || 'unknown'
}

const broadcastOrdersCacheMutation = ({ type, id: mutationId, patch = null }) => {
  if (!type || !mutationId) return

  const mutationPayload = {
    type,
    id: mutationId,
    patch,
    user_id: getCurrentUserIdForMutation(),
    source_tab_id: getOrdersTabId(),
    timestamp: Date.now(),
  }

  // Same browser / other tabs: no backend request needed.
  localStorage.setItem(ORDERS_CACHE_MUTATION_KEY, JSON.stringify(mutationPayload))

  // Modern tab sync. This reaches other open tabs on the same browser.
  if (typeof BroadcastChannel !== 'undefined') {
    try {
      const channel = new BroadcastChannel(ORDERS_CACHE_CHANNEL)
      channel.postMessage(mutationPayload)
      channel.close()
    } catch (error) {
      console.debug('[Orders Sync] BroadcastChannel failed:', error.message)
    }
  }

  // Same-tab custom event fallback. Open.js can listen to this if needed.
  try {
    window.dispatchEvent(
      new CustomEvent('orders-cache-mutation', {
        detail: mutationPayload,
      }),
    )
  } catch (error) {
    console.debug('[Orders Sync] CustomEvent failed:', error.message)
  }
}

/* ───────────────────── IndexedDB Xero Contact Helper ───────────────────── */
const getMatchingXeroContactFromIndexedDB = (accountName) => {
  return new Promise((resolve) => {
    if (!accountName) {
      resolve(null)
      return
    }

    const request = indexedDB.open('XeroContactsCacheDB')

    request.onerror = () => {
      console.error('IndexedDB open error:', request.error)
      resolve(null)
    }

    request.onsuccess = () => {
      const db = request.result

      try {
        if (!db.objectStoreNames.contains('contacts')) {
          console.error('IndexedDB store "contacts" not found')
          db.close()
          resolve(null)
          return
        }

        const transaction = db.transaction(['contacts'], 'readonly')
        const store = transaction.objectStore('contacts')
        const getRequest = store.get('xero_contacts_cache')

        getRequest.onerror = () => {
          console.error('IndexedDB get error:', getRequest.error)
          db.close()
          resolve(null)
        }

        getRequest.onsuccess = () => {
          const cachedData = getRequest.result

          const contacts =
            cachedData?.data?.body?.body?.Contacts ||
            cachedData?.data?.body?.body?.COntacts ||
            cachedData?.data?.body?.Contacts ||
            cachedData?.body?.body?.Contacts ||
            cachedData?.body?.Contacts ||
            []

          const customerAccountName = accountName.trim().toLowerCase()

          const matchingContact = contacts.find((contact) => {
            const xeroName = contact?.Name || contact?.AccountName || contact?.ContactName || ''

            return xeroName.trim().toLowerCase() === customerAccountName
          })

          db.close()
          resolve(matchingContact || null)
        }
      } catch (error) {
        console.error('IndexedDB contact match error:', error)
        db.close()
        resolve(null)
      }
    }
  })
}

/* ───────────────────── helper: pick one badge ───────────────────── */
const getStatusBadge = ({ order_status, dockets_ready, dockets_complete, tons_complete }) => {
  if (Number(order_status) === 3) {
    return { text: 'Cancelled', className: 'bg-danger' }
  }

  if (
    Number(order_status) === 1 ||
    (String(dockets_complete) === '1' && String(tons_complete) === '1')
  ) {
    return { text: 'Completed', className: 'bg-success' }
  }

  if (String(dockets_complete) === '1') {
    return { text: 'Dockets Complete', className: 'bg-info text-dark' }
  }

  if (String(tons_complete) === '1') {
    return { text: 'Tons Complete', className: 'bg-info text-dark' }
  }

  switch (String(dockets_ready)) {
    case '1':
      return { text: 'Ready', className: 'bg-secondary' }

    case '2':
      return { text: 'Will Advise', className: 'bg-warning text-dark' }

    case '3':
      return { text: 'In Progress', className: 'bg-primary' }

    case '':
    case '0':
    default:
      return { text: 'In Progress', className: 'bg-primary' }
  }
}

const TableRow = ({ item, index, xeroContacts }) => {
  const [indexedDbMatchingContact, setIndexedDbMatchingContact] = useState(null)
  const [isDeleting, setIsDeleting] = useState(false)
  const user = useContext(Usercntxt)

  useEffect(() => {
    let isMounted = true

    const loadMatchingContact = async () => {
      const matchingContact = await getMatchingXeroContactFromIndexedDB(item.customer?.account_name)

      if (!isMounted) return

      setIndexedDbMatchingContact(matchingContact)
    }

    loadMatchingContact()

    return () => {
      isMounted = false
    }
  }, [item.customer?.account_name])

  const deleteOrder = async (e, id) => {
    e.preventDefault()

    if (isDeleting) return

    const result = await swal.fire({
      title: 'Are you sure you want to proceed?',
      text: 'This will completely delete the order permanently',
      icon: 'warning',
      showDenyButton: true,
      denyButtonText: 'Cancel!',
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Yes, Proceed!',
    })

    if (result.isDenied) {
      swal.fire('Changes are not saved', '', 'info')
      return
    }

    if (!result.isConfirmed) return

    setIsDeleting(true)

    try {
      const sourceTabId = getOrdersTabId()

      const res = await axios.delete(
        `/api/delete_order/${id}?source_tab_id=${encodeURIComponent(sourceTabId)}`,
        {
          data: {
            source_tab_id: sourceTabId,
          },
        },
      )

      if (res.data.status === 200) {
        broadcastOrdersCacheMutation({
          type: 'delete',
          id,
        })

        await swal.fire('Deleted!', res.data.message, 'success')
        return
      }

      if (res.data.status === 404) {
        await swal.fire('Error', res.data.message, 'error')
        return
      }

      await swal.fire('Error', res.data.message || 'Order could not be deleted', 'error')
    } catch (error) {
      console.error('Delete order failed:', error)

      await swal.fire({
        icon: 'error',
        title: 'Delete Failed',
        text:
          error?.response?.data?.message ||
          error?.response?.data?.error ||
          'Something went wrong while deleting the order.',
      })
    } finally {
      setIsDeleting(false)
    }
  }

  const { text: badgeText, className: badgeColor } = getStatusBadge(item)

  return (
    <tr key={item.id}>
      <td data-order={item.id}>#{item.id}</td>

      <td>{item?.customer?.code}</td>

      <td>
        <strong>{item?.customer?.account_name}</strong>
        <br />

        {indexedDbMatchingContact ? (
          <>
            {['DDI', 'DEFAULT', 'FAX', 'MOBILE'].map((type) => {
              const phone = indexedDbMatchingContact.Phones?.find((p) => p.PhoneType === type)

              if (!phone || !phone.PhoneNumber) return null

              const fullPhone = `${phone.PhoneCountryCode || ''} ${phone.PhoneAreaCode || ''} ${
                phone.PhoneNumber
              }`.trim()

              const charPart = fullPhone
                .split('')
                .filter((c) => !/[0-9\s]/.test(c))
                .join('')

              const digitPart = fullPhone
                .split('')
                .filter((c) => /[0-9\s]/.test(c))
                .join('')

              return (
                <div key={type}>
                  <strong>{type}:</strong>
                  <br />
                  <span>{charPart}</span>
                  <br />
                  <span style={{ whiteSpace: 'pre' }}>{digitPart}</span>
                </div>
              )
            })}
          </>
        ) : (
          <span>No Xero Contact</span>
        )}
      </td>

      <td>
        <div>
          {item.customer?.customer_address?.map((addr, addrIndex) =>
            addr.contacts?.map((contact, i) => {
              if (!contact.contact_name && !contact.contact_number && !contact.id) return null

              return (
                <div key={`${addrIndex}-${i}`}>
                  {contact.contact_name && (
                    <>
                      <strong>Name:</strong> {contact.contact_name}
                      <br />
                    </>
                  )}

                  {contact.contact_number && (
                    <>
                      <strong>Number:</strong> {contact.contact_number}
                      <br />
                    </>
                  )}

                  <hr className="my-1" />
                </div>
              )
            }),
          )}
        </div>
      </td>

      <td>
        <strong>{item?.order_note}</strong>
      </td>

      <td>
        {item.order_list?.map((list, li) =>
          list.docket_numbers?.slice(0, 5).map((docket, di) => (
            <div key={`${li}-${di}`}>
              <strong>DN:</strong> {docket.docket_number}
            </div>
          )),
        )}
      </td>

      <td>
        {item.order_list?.map((p, i) => (
          <div key={i}>
            {p.order_list_products
              ?.filter((code) => !code.is_dummy && code.product_name !== 'FRE')
              .map((code, ind) => (
                <span className="badge bg-dark mx-1" key={ind}>
                  {code.product_name}
                </span>
              ))}
          </div>
        ))}
      </td>

      <td>
        <strong>TO </strong>
        {item.tons_ordered || 'N/A'} <strong>TA </strong>
        {item.tons_actual || 'N/A'}
      </td>

      <td>{item.orderdeliveryzone?.name}</td>

      <td>
        <strong>{item.machine_id === null ? 'N/A' : item.machine?.name}</strong>

        {item.operator?.map((op, i) => (
          <p key={i} className="m-0">
            {op.operator?.fname} {op.operator?.lname}
          </p>
        ))}
      </td>

      <td>
        <span className={`badge mx-1 ${badgeColor}`}>{badgeText}</span>
      </td>

      <td data-order={item.updated_at}>
        {item.updated_at ? format(new Date(item.updated_at), 'dd/MM/yyyy | HH:mm:ss') : 'N/A'}
      </td>

      <td>
        <div className="d-flex">
          {user.can('access orders') && (
            <Link
              to={`/orders/update/${item.id}`}
              target="_blank"
              rel="noopener noreferrer"
              className="btn btn-outline-info btn-sm"
            >
              <i className="far fa-edit" />
            </Link>
          )}

          {user.can('access orders') && (
            <button
              type="button"
              onClick={(e) => deleteOrder(e, item.id)}
              className="btn btn-outline-danger btn-sm ms-2"
              disabled={isDeleting}
            >
              {isDeleting ? (
                <span
                  className="spinner-border spinner-border-sm"
                  role="status"
                  aria-hidden="true"
                />
              ) : (
                <i className="far fa-trash-alt" />
              )}
            </button>
          )}
        </div>
      </td>
    </tr>
  )
}

export default TableRow
